import { useQuery } from "@tanstack/react-query";
import React from "react";

import { INSIGHTS_BACKEND_URL, NEST_BACKEND_URL } from "@/lib/axios";
import { QueryConfig } from "@/lib/react-query";
import { ComparisonMode } from "@/pages/investment-levels/types/comparisonMode";
import { useModuleEndpoint } from "@/shared/hooks/useModuleEndpoint";
import { axiosApi } from "@/Utils";

export const getChannelsOld = (
  type: "all" | "saturation-curves" | "flighting" = "all",
  startDate?: string | null,
  endDate?: string | null,
  brand?: string,
  category?: string,
  product?: string,
  geography?: string,
  geographies?: string[],
  funnelStages?: string[]
): Promise<string[]> => {
  const url = type === "all" ? "/api/v2/channels" : `/api/v2/channels/${type}`;
  return axiosApi.get(url, {
    params: { startDate, endDate, brand, category, product, geography, geographies, funnelStages },
    baseURL: INSIGHTS_BACKEND_URL,
  });
};

export const getChannelsNew = (
  type: "all" | "saturation-curves" | "flighting" = "all",
  startDate?: string | null,
  endDate?: string | null,
  brand?: string,
  category?: string,
  product?: string,
  geography?: string,
  geographies?: string[],
  funnelStages?: string[]
): Promise<string[]> => {
  const url = type === "all" ? "/api/channels" : `/api/channels/${type}`;

  const params = new URLSearchParams();

  if (startDate) params.append("startDate", startDate);
  if (endDate) params.append("endDate", endDate);
  if (brand) params.append("brand", brand);
  if (category) params.append("category", category);
  if (product) params.append("product", product);
  if (geography) params.append("geography", geography);

  if (geographies && geographies.length > 0) {
    geographies.forEach((geo) => {
      params.append("geographies", geo);
    });
  }

  if (funnelStages && funnelStages.length > 0) {
    funnelStages.forEach((stage) => {
      params.append("funnelStages", stage);
    });
  }

  return axiosApi.get(url, {
    params,
    baseURL: NEST_BACKEND_URL,
  });
};

export const useChannels = ({
  type,
  startDate,
  endDate,
  config,
  brand,
  category,
  product,
  geography,
  geographies,
  funnelStages,
  comparisonMode,
}: {
  type: "all" | "saturation-curves" | "flighting";
  startDate?: string | null;
  endDate?: string | null;
  brand?: string;
  category?: string;
  product?: string;
  geography?: string;
  geographies?: string[];
  funnelStages?: string[];
  comparisonMode?: ComparisonMode;
  config?: QueryConfig<typeof getChannelsOld | typeof getChannelsNew>;
}) => {
  const endpointType = useModuleEndpoint("channel-roi");
  const queryKey = React.useMemo(
    () => [
      "channels",
      endpointType,
      type,
      startDate,
      endDate,
      brand,
      category,
      product,
      geography,
      geographies,
      funnelStages,
      comparisonMode,
    ],
    [
      endpointType,
      type,
      startDate,
      endDate,
      brand,
      category,
      product,
      geography,
      geographies,
      funnelStages,
      comparisonMode,
    ]
  );

  const queryFn = React.useCallback(() => {
    if (endpointType === "new") {
      return getChannelsNew(
        type,
        startDate,
        endDate,
        brand,
        category,
        product,
        geography,
        geographies,
        funnelStages
      );
    } else {
      return getChannelsOld(
        type,
        startDate,
        endDate,
        brand,
        category,
        product,
        geography,
        geographies,
        funnelStages
      );
    }
  }, [
    endpointType,
    type,
    startDate,
    endDate,
    brand,
    category,
    product,
    geography,
    geographies,
    funnelStages,
  ]);

  return useQuery({
    ...config,
    queryKey,
    queryFn,
  });
};
